import React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Lede } from '@/components/ui/calm';
import { ExplainButton } from '@/components/qbee/qbee-buttons';

/**
 * The table header language, codified once (the Quantro-style reference):
 * a faint wash behind the header row, 11px medium muted labels, and a sort
 * affordance on every sortable column - idle columns show the double chevron
 * ghosted in, the active one shows a single solid arrow for its direction.
 *
 * Sorting stays the CALLER's state (URL param, server param, local state -
 * whatever that table already uses); this only draws it and reports clicks.
 * A visual kit that owned the data would be a second table library.
 */

/**
 * THE CLEAN TABLE (owner's reference, 4 Oct 2026): a soft band behind a
 * header in plain dark type, one value per cell sitting tight in a 36px
 * row (8px above and below, no more), hairlines between rows, and colour
 * kept for the number itself, never for the cell. A reader should be able to run an eye down a column and compare.
 * One set of class names so a table restyled to it cannot drift from the
 * next; apply them to a plain <table>, numbers right-aligned with
 * tabular-nums as everywhere else.
 */
export const CLEAN_TABLE = {
  wrap: 'overflow-x-auto rounded-lg border border-[hsl(var(--card-border))] bg-card',
  table: 'w-full text-[14px]',
  // foreground at 5%: a band that reads as a band on the light and the dark theme (muted alone was nearly white).
  head: 'bg-[hsl(var(--surface-subtle))] text-left text-[13.5px] font-medium text-muted-foreground [&_th]:first-letter:uppercase',
  th: 'px-4 py-2 font-medium',
  row: 'border-t border-[hsl(var(--border-light))]',
  td: 'px-4 py-2',
};

/**
 * A VALUE AND ITS SMALL FIGURE, ON ONE LINE.
 *
 * A second line under a value makes a 56px row however it is styled, and a
 * table of 56px rows is heavy to read down. So the second fact sits BESIDE
 * the value, in a slot of fixed width: the values of a column still end on
 * one edge, the small figures line up in a column of their own, and an empty
 * slot keeps its width so nothing shifts. The row stays one line high.
 * What the small figure means is said once, above the table, never in each
 * cell and never by lengthening a header.
 *
 *   tone   'good' | 'bad' | 'muted' (default): colour of the small figure
 *   slot   width of the small figure's slot, a Tailwind class (default w-12)
 *   strong the value in medium weight: the column the table is sorted by
 */
const SIDE_TONE = {
  good: 'text-emerald-700 dark:text-emerald-400',
  bad: 'text-rose-700 dark:text-rose-400',
  muted: 'text-muted-foreground',
};
export function CellInline({ value, side, tone = 'muted', strong = false, slot = 'w-12', className, title }) {
  return (
    <span title={title} className={cn('inline-flex items-baseline justify-end gap-2 whitespace-nowrap tabular-nums', className)}>
      <span className={cn('text-foreground', strong && 'font-medium')}>{value}</span>
      <span className={cn('shrink-0 text-left text-[13px]', slot, SIDE_TONE[tone] || SIDE_TONE.muted)}>{side || ''}</span>
    </span>
  );
}

export function THead({ children, className }) {
  return (
    // The clean header (owner, 4 Oct 2026): a soft band, plain dark type at
    // 14px. The band is what marks the row as a header; before it, the header
    // needed semibold to stand apart from the data and still read as a first
    // row in a smaller size. This is the one place every kit table's header
    // is set, so it lands everywhere at once.
    <thead className={cn('bg-[hsl(var(--surface-subtle))] text-left text-[13.5px] font-medium [&_th]:first-letter:uppercase text-muted-foreground', className)}>
      {children}
    </thead>
  );
}

/**
 * One sortable header cell.
 *  - `dir`: 'asc' | 'desc' | null - this column's CURRENT direction (null = not
 *    the active sort). Renders the ghost chevrons when null.
 *  - `onSort(nextDir)`: called with 'desc' first, then 'asc' - "biggest first"
 *    is the right first click for every money/score column this app has.
 *  - omit onSort for a plain, unsortable cell with the same typography.
 */
export function Th({ children, dir = null, onSort, align = 'left', className }) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  if (!onSort) {
    return <th className={cn('px-4 py-2 font-semibold', alignCls, className)}>{children}</th>;
  }
  return (
    <th className={cn('p-0', alignCls, className)}>
      <button
        type="button"
        onClick={() => onSort(dir === 'desc' ? 'asc' : 'desc')}
        aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
        className={cn(
          'group flex w-full items-center gap-1 px-4 py-2 font-medium transition-colors hover:text-foreground',
          align === 'right' && 'justify-end',
          align === 'center' && 'justify-center',
          dir && 'text-foreground'
        )}
      >
        <span className="truncate">{children}</span>
        {dir === 'desc' ? <ArrowDown className="size-3 shrink-0 text-primary" />
          : dir === 'asc' ? <ArrowUp className="size-3 shrink-0 text-primary" />
            : <ChevronsUpDown className="size-3 shrink-0 opacity-40 transition-opacity group-hover:opacity-80" />}
      </button>
    </th>
  );
}

/**
 * A page section with the reference's rhythm: title + muted subtitle, content,
 * and a hairline divider before the NEXT section. Use inside a SectionStack so
 * the divider logic (never after the last) lives in one place.
 */
export function SectionStack({ children, className }) {
  return <div className={cn('divide-y divide-border/70', className)}>{children}</div>;
}

export function PageSection({ title, description, right, children, className }) {
  return (
    <section className={cn('py-6 first:pt-0 last:pb-0', className)}>
      {(title || right) && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            {title && <h3 className="font-display text-[17px] font-semibold tracking-[-0.01em] text-foreground">{title}<ExplainButton title={title} className="ml-1 -translate-y-px" /></h3>}
            {description && (
              <Lede text={description} className="mt-0.5 max-w-2xl text-[13.5px] leading-relaxed text-muted-foreground" />
            )}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export default Th;
